
import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Calendar as CalendarIcon, Filter, X } from 'lucide-react';
import { format } from 'date-fns';

interface MediaFiltersProps {
  selectedTags?: string[];
  onTagToggle?: (tag: string) => void;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}

const MediaFilters: React.FC<MediaFiltersProps> = ({ 
  selectedTags = [], 
  onTagToggle = () => {},
  activeTab = 'all',
  setActiveTab = () => {}
}) => {
  const [dateRange, setDateRange] = React.useState<Date | undefined>(undefined);
  const [selectedFormat, setSelectedFormat] = React.useState<string>('all');
  
  const clearAllFilters = () => {
    setDateRange(undefined);
    setSelectedFormat('all');
    // Clear any selected tags
    selectedTags.forEach(tag => onTagToggle(tag));
  };
  
  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Filters</h3>
        
        {(selectedTags.length > 0 || dateRange || selectedFormat !== 'all') && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-8 px-2">
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium block mb-2">Date Added</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange ? format(dateRange, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateRange}
                onSelect={setDateRange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div>
          <label className="text-sm font-medium block mb-2">File Format</label>
          <Select value={selectedFormat} onValueChange={setSelectedFormat}>
            <SelectTrigger>
              <SelectValue placeholder="All Formats" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Formats</SelectItem>
              <SelectItem value="wav">WAV</SelectItem>
              <SelectItem value="mp3">MP3</SelectItem>
              <SelectItem value="aiff">AIFF</SelectItem>
              <SelectItem value="flac">FLAC</SelectItem>
              <SelectItem value="midi">MIDI</SelectItem>
              <SelectItem value="jpg">JPG</SelectItem>
              <SelectItem value="png">PNG</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Separator />
        
        <div>
          <label className="text-sm font-medium block mb-2">Duration</label>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm">
              &lt; 2 min
            </Button>
            <Button variant="outline" size="sm">
              2-5 min
            </Button>
            <Button variant="outline" size="sm">
              5-10 min
            </Button>
            <Button variant="outline" size="sm">
              &gt; 10 min
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaFilters;
